import React from 'react';
import { DeadpanItemId, formatString, getLanguage } from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

const HINT_ANNOUNCEMENT_ITEM_IDS = new Set<DeadpanItemId>([
  DeadpanItemId.CATEGORY_HINT,
  DeadpanItemId.COINFLIP_CATEGORY,
  DeadpanItemId.USELESS_HINT,
]);

function getAnnouncementStorageKey(roomCode?: string): string | null {
  return roomCode ? `deadpan:last-announcement:${roomCode}` : null;
}

function getLastShownAnnouncementId(storageKey: string | null): string | null {
  if (!storageKey) return null;
  try {
    return window.sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function setLastShownAnnouncementId(storageKey: string | null, announcementId: string): void {
  if (!storageKey) return;
  try {
    window.sessionStorage.setItem(storageKey, announcementId);
  } catch {
    // Ignore storage access errors and fall back to in-memory visibility only.
  }
}

export const DeadpanAnnouncementLine: React.FC = () => {
  const { gameState, currentRoom, myPlayerId } = useSocket();
  const isZh = getLanguage() === 'zh';
  const t = useTranslations();
  const displayAnnouncement = React.useMemo(
    () => gameState?.deadpanItemAnnouncements
      ?.slice()
      .reverse()
      .find((ann) => !ann.actorPlayerId || ann.actorPlayerId === myPlayerId),
    [gameState?.deadpanItemAnnouncements, myPlayerId],
  );
  const [visibleAnnouncementId, setVisibleAnnouncementId] = React.useState<string | null>(null);
  const storageKey = React.useMemo(
    () => getAnnouncementStorageKey(currentRoom?.code),
    [currentRoom?.code],
  );

  React.useEffect(() => {
    if (!displayAnnouncement?.id || currentRoom?.gameType !== 'deadpan') {
      setVisibleAnnouncementId(null);
      return;
    }

    if (getLastShownAnnouncementId(storageKey) === displayAnnouncement.id) {
      setVisibleAnnouncementId(null);
      return;
    }

    setVisibleAnnouncementId(displayAnnouncement.id);
    setLastShownAnnouncementId(storageKey, displayAnnouncement.id);

    const timeout = window.setTimeout(() => {
      setVisibleAnnouncementId((current) => (current === displayAnnouncement.id ? null : current));
    }, 4200);

    return () => window.clearTimeout(timeout);
  }, [displayAnnouncement?.id, currentRoom?.gameType, storageKey]);

  const isVisible = displayAnnouncement?.id != null && displayAnnouncement.id === visibleAnnouncementId;
  const actorLabel = displayAnnouncement?.actorName || t.games.deadpan.items.announcementMysteryActor;

  const text = (() => {
    const ann = displayAnnouncement;
    if (!ann) return '';
    const isHintItem = HINT_ANNOUNCEMENT_ITEM_IDS.has(ann.itemId);
    const showSelfLine =
      isHintItem &&
      myPlayerId != null &&
      ann.actorPlayerId === myPlayerId &&
      ann.selfOnlyEffect != null &&
      ann.selfOnlyEffectZh != null;
    if (showSelfLine) {
      return isZh ? ann.selfOnlyEffectZh : ann.selfOnlyEffect;
    }
    return formatString(t.games.deadpan.items.announcementTemplate, {
      actor: actorLabel,
      title: isZh ? ann.titleZh : ann.title,
      effect: isZh ? ann.effectZh : ann.effect,
    });
  })();

  return (
    <div className="mt-2 min-h-6 w-full">
      <p className={`text-sm text-violet-700 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        {text || ' '}
      </p>
    </div>
  );
};
